import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel';
import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { AuditTab } from '@/components/ui/audit-tab';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { VehicleDisplayItem, VehicleDisplayList } from '@/components/ui/vehicle-display-item';
import { ParkingSessionsListPanel } from '@/pages/parking-sessions/parking-sessions-list-panel';
import { CONTRACT_EFFECTIVE_VEHICLES_QUERY } from './contracts.gql';
import { useLazyQuery } from '@apollo/client';
import { Car, ChevronUp, FileText, History, Loader2, Shield } from 'lucide-react';
import { ContractGuaranteesTab } from './guarantees/contract-guarantees-tab';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContractListItem } from './types';
import { ContractStatusBadge } from './status-badge';
import { formatCurrency, formatDateRange } from './utils';

interface ContractDetailsProps {
	contract: ContractListItem;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
	onContractRefresh?: () => void;
}

const getCustomerDisplayName = (contract: ContractListItem) => {
	const customer = contract.customer;

	if (customer.isCompany) {
		return customer.firmName ?? `#${contract.contractId.slice(-6)}`;
	}

	const fullName = [customer.firstName, customer.lastName]
		.filter(Boolean)
		.join(' ');

	return fullName || customer.firmName || `#${contract.contractId.slice(-6)}`;
};

export const ContractEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<FileText className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const ContractDetailsHeader = ({
	contract,
	className,
	...actions
}: {
	contract: ContractListItem;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tC } = useTranslation('contracts');
	const displayName = getCustomerDisplayName(contract);
	const contractSubtitle = contract.contractNumber
		? `#${contract.contractNumber}`
		: contract.isActive
			? tC('details.activeContract')
			: tC('details.inactiveContract');

	return (
		<EntityDetailsHeader
			icon={<ContractEntityIcon />}
			name={displayName}
			subtitle={contractSubtitle}
			statusBadge={
				contract.status === 'active'
					? undefined
					: <ContractStatusBadge status={contract.status} />
			}
			entityId={actions.entityId ?? contract.contractId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

interface EffectiveVehicle {
	vehicleId: string;
	plateNumber?: string | null;
	make?: string | null;
	model?: string | null;
	color?: string | null;
	vehicleType?: { name?: string | null } | null;
}

const EffectiveVehiclesLazy = ({ contractId }: { contractId: string }) => {
	const { t: tC } = useTranslation('contracts');
	const [expanded, setExpanded] = useState(false);
	const [fetchEffectiveVehicles, { data, loading }] = useLazyQuery<{
		contract: { effectiveVehicles: EffectiveVehicle[] } | null;
	}>(CONTRACT_EFFECTIVE_VEHICLES_QUERY, {
		fetchPolicy: 'cache-first',
	});

	const vehicles = data?.contract?.effectiveVehicles ?? [];

	const handleShowAll = useCallback(() => {
		setExpanded(true);
		fetchEffectiveVehicles({ variables: { contractId } });
	}, [contractId, fetchEffectiveVehicles]);

	const handleHide = useCallback(() => {
		setExpanded(false);
	}, []);

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<span className="text-sm text-gray-700 dark:text-slate-300">
					{tC('details.allCustomerVehiclesIncluded', 'All customer vehicles are included in this contract.')}
				</span>
				{!expanded ? (
					<button
						type="button"
						onClick={handleShowAll}
						className="text-xs font-medium text-primary hover:text-primary/80 transition-colors whitespace-nowrap ml-3"
					>
						{tC('details.showAllVehicles', 'Show all')}
					</button>
				) : (
					<button
						type="button"
						onClick={handleHide}
						className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors whitespace-nowrap ml-3"
					>
						<ChevronUp className="h-3 w-3" />
						{tC('details.hideVehicles', 'Hide')}
					</button>
				)}
			</div>
			{expanded && (
				<div>
					{loading ? (
						<div className="flex items-center gap-2 py-3 text-sm text-gray-500 dark:text-slate-400">
							<Loader2 className="h-4 w-4 animate-spin" />
							{tC('details.loadingEffectiveVehicles', 'Loading vehicles...')}
						</div>
					) : vehicles.length === 0 ? (
						<div className="text-sm text-gray-500">
							{tC('details.noEffectiveVehicles', 'No effective vehicles found.')}
						</div>
					) : (
						<VehicleDisplayList>
							{vehicles.map(vehicle => (
								<VehicleDisplayItem
									key={vehicle.vehicleId}
									vehicleId={vehicle.vehicleId}
									plateNumber={vehicle.plateNumber}
									vehicleTypeName={vehicle.vehicleType?.name}
									make={vehicle.make}
									model={vehicle.model}
									color={vehicle.color}
									className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
								/>
							))}
						</VehicleDisplayList>
					)}
				</div>
			)}
		</div>
	);
};

export const ContractDetails = ({
	contract,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton,
	onContractRefresh
}: ContractDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tC } = useTranslation('contracts');
	const [activeTab, setActiveTab] = useState('details');
	const { customer } = contract;
	const effectiveVehicleLimit =
		contract.simultaneousVehicleLimit ?? customer.categoryLimit ?? null;
	const assignedVehicleRowCount = contract.includeAllCustomerVehicles
		? undefined
		: contract.vehicles.length;
	return (
		<div className="flex flex-col h-full min-h-0">
			{showHeader && (
				<ContractDetailsHeader
					contract={contract}
					entityId={contract.contractId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

			<Tabs
				value={activeTab}
				onValueChange={setActiveTab}
				className="flex flex-col h-full min-h-0"
			>
				{/* Tab Bar - fixed height at top */}
				<div className="flex-shrink-0 pb-2">
					<TabsList>
						<TabLink value="details" icon={FileText} label={t('tabs.details')} />
						<TabLink value="guarantees" icon={Shield} label={t('tabs.guarantees')} />
						<TabLink value="parkingSessions" icon={Car} label={t('tabs.parkingSessions')} />
						<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
					</TabsList>
				</div>

				{/* Tab Content - scrollable area with min-height for stable modal sizing */}
				<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
					<TabsContent value="details" className="space-y-6 mt-0">
						<DetailSection>
							<DetailFieldsGrid>
								<DetailField
									label={tC('details.validityPeriod')}
									value={formatDateRange(
										contract.beginDate,
										contract.endDate
									)}
								/>
								<DetailField
									label={tC('details.monthlyAmount')}
									value={formatCurrency(contract.amount ?? null)}
								/>
							</DetailFieldsGrid>
						</DetailSection>

						<DetailSection title={t('sections.renewal_access')}>
							<DetailFieldsGrid>
								<DetailField
									label={tC('details.autoRenewal')}
									value={
										contract.isAutoRenewed
											? tC('details.enabled')
											: tC('details.disabled')
									}
								/>
								{contract.preferredLanguage && (
									<DetailField
										label={tC('details.preferredLanguage')}
										value={
											contract.preferredLanguage === 'EN'
												? tC('form.preferredLanguageEN')
												: tC('form.preferredLanguageEL')
										}
									/>
								)}
								<DetailField
									label={tC('details.effectiveVehicleLimit')}
									value={
										effectiveVehicleLimit != null
											? `${effectiveVehicleLimit} ${tC('details.concurrentVehicles')}`
											: t('placeholders.inherit_from_customer')
									}
								/>
								<DetailField
									label={tC('details.reservedParkingSlots')}
									value={
										contract.reservedParkingSlots != null
											? `${contract.reservedParkingSlots} ${tC('details.slotsUnit')}`
											: tC('details.notDefined')
									}
								/>
								<DetailField
									label={tC('details.customerCategory')}
									value={customer.categoryName}
								/>
								<DetailField
									label={tC('details.categoryLimit')}
									value={
										customer.categoryLimit != null
											? `${customer.categoryLimit} ${tC('details.vehiclesUnit')}`
											: tC('details.notDefined')
									}
								/>
							</DetailFieldsGrid>
						</DetailSection>

						<DetailSection
							title={tC('details.assignedVehicles', 'Assigned Vehicles')}
							rowCount={assignedVehicleRowCount}
						>
							{contract.includeAllCustomerVehicles ? (
								<EffectiveVehiclesLazy contractId={contract.contractId} />
							) : contract.vehicles.length === 0 ? (
								<div className="text-sm text-gray-500">
									{tC('details.noVehicles')}
								</div>
							) : (
								<VehicleDisplayList>
									{contract.vehicles.map(vehicle => (
										<VehicleDisplayItem
											key={vehicle.vehicleId}
											vehicleId={vehicle.vehicleId}
											plateNumber={vehicle.plateNumber}
											vehicleTypeName={vehicle.vehicleTypeName}
											make={vehicle.make}
											model={vehicle.model}
											color={vehicle.color}
											className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
										/>
									))}
								</VehicleDisplayList>
							)}
						</DetailSection>

						{contract.vipVehicles.length > 0 && (
							<DetailSection
								title={tC('details.vipVehicles', 'VIP Vehicles')}
								rowCount={contract.vipVehicles.length}
							>
								<VehicleDisplayList>
									{contract.vipVehicles.map(vehicle => (
										<VehicleDisplayItem
											key={vehicle.vehicleId}
											vehicleId={vehicle.vehicleId}
											plateNumber={vehicle.plateNumber}
											vehicleTypeName={vehicle.vehicleTypeName}
											make={vehicle.make}
											model={vehicle.model}
											color={vehicle.color}
											className="py-3 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
										/>
									))}
								</VehicleDisplayList>
							</DetailSection>
						)}

						{/* Access Cards */}
						<DetailSection
							title={tC('details.accessCards', 'Access Cards')}
							rowCount={contract.cards.length}
						>
							{contract.cards.length === 0 ? (
								<div className="text-sm text-gray-500">{tC('details.noCards', 'No access cards assigned.')}</div>
							) : (
								<div className="space-y-2">
									{contract.cards.map((card, idx) => (
										<div
											key={card.contractCardId ?? `${card.cardType}-${card.cardKey ?? idx}`}
											className="flex items-center justify-between py-2 border-b-2 border-dotted border-border/60 last:border-b-0"
										>
											<div className="flex items-center gap-3">
												<Badge variant="outline" className="font-mono text-xs">
													{card.cardType}
												</Badge>
												<span className="text-sm font-mono">{card.cardKey ?? '—'}</span>
											</div>
											{card.cardStatusId !== 'ACTIVE' && (
												<Badge variant="secondary">
													{card.cardStatusId}
												</Badge>
											)}
										</div>
									))}
								</div>
							)}
						</DetailSection>
					</TabsContent>

					<TabsContent value="parkingSessions" className="mt-0">
						<ParkingSessionsListPanel
							contractId={contract.contractId}
							isActive={activeTab === 'parkingSessions'}
						/>
					</TabsContent>

					<TabsContent value="guarantees" className="mt-0">
						<ContractGuaranteesTab
							cards={contract.cards}
							isActive={activeTab === 'guarantees'}
							onContractRefresh={onContractRefresh}
						/>
					</TabsContent>

					<TabsContent value="audit" className="mt-0">
						<AuditTab
							data={
								{
									// Contract audit fields would come from the contract data
									// Adding placeholders for now since they're not in the current type
								}
							}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};
